export interface IncompleteTwinDelivery {
  completed: number
  requested: number
  failed: number
}

function finiteCount(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null
  }
  return value
}

function outcomeSummary(metadata: unknown): Record<string, unknown> | null {
  if (!metadata || typeof metadata !== "object") {
    return null
  }
  const summary = (metadata as { respondent_outcome_summary?: unknown })
    .respondent_outcome_summary
  if (!summary || typeof summary !== "object") {
    return null
  }
  return summary as Record<string, unknown>
}

/**
 * Shortfall from the simulation ledger. Warn when failed or cancelled twins
 * leave fewer responses than were requested. The omitted count is
 * requested minus succeeded, so a mix of the two is not undercounted.
 *
 * A missing ledger is not a failure: legacy results omit it. A ledger that
 * recorded no failures and no cancellations does not warn, including
 * `complete: false` with both of those counts at zero.
 */
export function incompleteTwinDelivery(
  metadata: unknown,
): IncompleteTwinDelivery | null {
  const summary = outcomeSummary(metadata)
  if (!summary) {
    return null
  }
  const failed = finiteCount(summary.failed)
  const succeeded = finiteCount(summary.succeeded)
  const requested = finiteCount(summary.requested)
  const cancelled = finiteCount(summary.cancelled) ?? 0
  if (failed === null || succeeded === null || requested === null) {
    return null
  }
  const terminalShortfall = Math.max(failed, 0) + Math.max(cancelled, 0)
  if (terminalShortfall <= 0 || requested <= 0 || succeeded >= requested) {
    return null
  }
  return {
    completed: succeeded,
    requested,
    failed: requested - succeeded,
  }
}

/** Count gap used when the run has no outcome ledger (study list). */
export function incompleteTwinDeliveryFromCounts(
  completed: number | null | undefined,
  requested: number | null | undefined,
): IncompleteTwinDelivery | null {
  if (typeof completed !== "number" || typeof requested !== "number") {
    return null
  }
  if (!Number.isFinite(completed) || !Number.isFinite(requested)) {
    return null
  }
  if (completed <= 0 || requested <= 0 || completed >= requested) {
    return null
  }
  return {
    completed,
    requested,
    failed: requested - completed,
  }
}

/**
 * Prefer the outcome ledger when the results file has one. Counts are only
 * a fallback for payloads that never recorded per-respondent status.
 *
 * The notice is for a finished run. A study that is still collecting must
 * not turn a live count gap into a dropped-twin warning. Every surface
 * passes `status` so that rule stays in one place.
 */
export function resolveIncompleteTwinDelivery(input: {
  metadata?: unknown
  completed?: number | null
  requested?: number | null
  status?: string | null
}): IncompleteTwinDelivery | null {
  if (input.status !== "completed") {
    return null
  }
  if (outcomeSummary(input.metadata)) {
    return incompleteTwinDelivery(input.metadata)
  }
  return incompleteTwinDeliveryFromCounts(input.completed, input.requested)
}
